import type { Dispatch } from '@reduxjs/toolkit';
import type { INotificationRequest } from '../../types';
import type {
  IPagination,
  IProductHierarchyRequest,
  IReadPropAssociations,
  ISavePropAssociations,
} from '../../types/IRequestTypes';
import { EApiType } from '../data/Constants';
import ApiUrl from '../services/APIUrl';
import { handleError } from '../services/ErrorHandle';
import { appSettings } from '../settings';
import AxiosClient from './AxiosClient';
import { handleSuccess } from './HttpUtil';

export const PdmApi = {
  /**
   * Fetches a paginated list of properties.
   * @param {IPagination} action - Pagination and filter parameters.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getProperties: (action: IPagination) => {
    return AxiosClient.post(ApiUrl.ReadProperties, action)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },

  /**
   * Fetches property associations based on the provided criteria.
   * @param {IReadPropAssociations} action - Criteria for fetching property associations.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getPropertyAssociations: (action: IReadPropAssociations, dispatch?: Dispatch) => {
    return AxiosClient.post(ApiUrl.ReadPropertyAssociation, action)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(err, EApiType.GetPropertyAssociations, dispatch);
      });
  },

  /**
   * Saves property associations.
   * @param {ISavePropAssociations} action - Data for saving property associations.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  savePropertyAssociations: (action: ISavePropAssociations, dispatch?: Dispatch) => {
    return AxiosClient.post(ApiUrl.CreatePropertyAssociation, action)
      .then((response) => {
        return handleSuccess(response, EApiType.SaveProperties, dispatch);
      })
      .catch((err) => {
        return handleError(err, EApiType.SaveProperties, dispatch);
      });
  },

  /**
   * Sends a notification using the notification API.
   * @param {INotificationRequest} action - Notification details.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  sendNotification: (action: INotificationRequest, dispatch?: Dispatch) => {
    return AxiosClient.post(ApiUrl.SendNotification, action)
      .then((response) => {
        return handleSuccess(response, EApiType.SendNotification, dispatch);
      })
      .catch((err) => {
        return handleError(err, EApiType.SendNotification, dispatch);
      });
  },

  /**
   * Fetches the list of values (LOV) from the API.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getListOfValues: () => {
    return AxiosClient.get(ApiUrl.ReadLov, {
      baseURL: appSettings.DisEndpoint,
    })
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },

  /**
   * Fetches the list of product hierarchies.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getProductHierarchy: () => {
    const payload: IProductHierarchyRequest = { search: '', agCodeOnly: true };
    return AxiosClient.post(ApiUrl.ReadProductHierarchy, payload, {
      baseURL: appSettings.DisEndpoint,
    })
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },
};
